import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import WheelDisplay from '../components/wheels/WheelDisplay';
import WheelEntryEditor from '../components/wheels/WheelEntryEditor';
import { useAuth } from '../hooks/useAuth';
import { createWheel } from '../services/databaseIntegration';
import { WheelEntry, WheelTheme } from '../types/wheel';
import { trackWheelCreation } from '../utils/trackingUtils';

/**
 * Page for creating a new wheel
 */
const CreateWheel: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // State for wheel data
  const [wheelName, setWheelName] = useState<string>('');
  const [entries, setEntries] = useState<WheelEntry[]>([]);

  // UI state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Validate wheel data before saving
   */
  const validateWheel = (): boolean => {
    if (!wheelName.trim()) {
      setError('Please enter a name for your wheel');
      return false;
    }

    if (entries.length < 2) {
      setError('Please add at least 2 entries to your wheel');
      return false;
    }

    setError(null);
    return true;
  };

  /**
   * Handle saving the wheel to Firebase or localStorage
   */
  const handleSaveWheel = async () => {
    if (!validateWheel()) return;

    try {
      setIsSaving(true);

      // Prepare wheel data
      const wheelData = {
        name: wheelName.trim(),
        entries: entries,
        theme: WheelTheme.DEFAULT, // Use default theme as specified in requirements
        category: 'custom',
        difficulty: 1,
        isPublic: false,
        wheelSettings: {
          allowDuplicateSpins: true,
          wheelStyle: 'default',
        },
      };

      // Save to Firebase if authenticated, or localStorage if not
      const wheelId = await createWheel(wheelData);

      // Track wheel creation event
      trackWheelCreation(wheelId, wheelData.name);

      // Navigate to the wheel detail page
      navigate(`/wheels/${wheelId}`);
    } catch (err) {
      console.error('Error saving wheel:', err);
      setError('Failed to save wheel. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to="/wheels"
          className="inline-flex items-center text-primary hover:text-primary-dark mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Wheels
        </Link>

        <h1 className="text-3xl font-bold text-dark mb-6">Create New Wheel</h1>

        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Storage location info */}
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-6">
          <p>
            {isAuthenticated
              ? 'You are signed in. Your wheel will be saved and accessible from any device.'
              : "You are not signed in. Your wheel will be saved to your browser's local storage only."}
          </p>
          {!isAuthenticated && (
            <p className="mt-2">
              <Link to="/sign-in" className="text-primary hover:underline">
                Sign in
              </Link>{' '}
              or{' '}
              <Link to="/sign-up" className="text-primary hover:underline">
                create an account
              </Link>{' '}
              to save wheels to the cloud.
            </p>
          )}
        </div>

        {/* Wheel Name Input */}
        <div className="mb-6">
          <label htmlFor="wheelName" className="block text-gray-700 font-medium mb-2">
            Wheel Name
          </label>
          <input
            type="text"
            id="wheelName"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter a name for your wheel"
            value={wheelName}
            onChange={e => setWheelName(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Wheel Display */}
          <div className="flex justify-center flex-col items-center">
            <WheelDisplay
              entries={entries}
              size={400}
              colorScheme={WheelTheme.DEFAULT}
              spinButtonText="Preview Spin"
            />
          </div>

          {/* Wheel Entries Section */}
          <div>
            <div className="card p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Wheel Entries</h2>
              <WheelEntryEditor entries={entries} onChange={setEntries} />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                type="button"
                className={`px-6 py-2 ${
                  isSaving ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                } text-white rounded-lg transition-colors`}
                disabled={isSaving}
                onClick={handleSaveWheel}
              >
                {isSaving ? (
                  <>
                    <span className="inline-block animate-spin mr-2">↻</span>
                    Saving...
                  </>
                ) : (
                  'Save Wheel'
                )}
              </button>
              <Link
                to="/wheels"
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateWheel;
